import React from 'react';
import { Play, Film, Camera, Undo2, AlertTriangle, Pencil, CornerDownRight, Image } from 'lucide-react';

/**
 * Every shot in the film and what it will actually use: a generated clip, a real
 * clip somebody chose, or nothing yet.
 *
 * ⚠️ IT IS DRIVEN BY THE SHOT LIST, NOT BY THE TAKES. The old grid drew one card
 * per generated take, so a shot the food rule sent to real photography did not
 * appear on the screen at all - the two shots waiting on a clip were invisible
 * until the assembler mentioned them. A shot with nothing is exactly the one
 * that needs to be looked at.
 *
 * ⚠️ REAL FOOTAGE IS MARKED AS REAL. We own film of our own warehouse, our own
 * watermen and our own boxes going out, and a viewer of this screen has to be
 * able to tell at a glance which shots are ours and which a model invented.
 *
 * ⚠️ SWAPPING BACK IS ALWAYS OFFERED WHERE A PAID TAKE EXISTS. Choosing real
 * footage does not delete the generated clip, so hiding the way back would make
 * a reversible decision feel permanent.
 */
export default function FootageGrid({ footage = [], busy, onPlay, onChoose, onRevert, onEdit, onInsertBefore, onUsePhoto }) {
  if (!footage.length) return null;
  const real = footage.filter((f) => f.kind === 'real').length;
  const none = footage.filter((f) => f.kind === 'none').length;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>The footage, shot by shot</div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          {real ? `${real} of ${footage.length} is real footage. ` : ''}
          {none ? `${none} still ${none === 1 ? 'has' : 'have'} no clip.` : ''}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
        {footage.map((f) => (
          <div key={f.number} style={{
            border: f.kind === 'none' ? '1px solid #f0c48a' : '1px solid var(--gray-200)',
            borderRadius: 6, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column',
          }}>
            {f.thumb ? (
              <button onClick={() => onPlay && onPlay(f)} title={`Play shot ${f.number}`}
                style={{ display: 'block', width: '100%', border: 'none', padding: 0, background: '#000', cursor: 'pointer', position: 'relative' }}>
                <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(f.thumb)}`}
                  alt={`Shot ${f.number}`} loading="lazy"
                  style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
                <span style={{
                  position: 'absolute', left: 6, top: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                  background: f.kind === 'real' ? 'rgba(26,127,55,0.92)' : 'rgba(0,57,92,0.86)', color: '#fff',
                }}>
                  {f.kind === 'real' ? <><Camera size={9} /> OURS</> : <><Film size={9} /> AI</>}
                </span>
                <Play size={22} color="#fff" style={{
                  position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.6))',
                }} />
              </button>
            ) : (
              <div style={{
                width: '100%', aspectRatio: '16/9', background: f.error ? '#fdecec' : '#fff8ee',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                color: f.error ? '#c0392b' : '#b25e09', fontSize: 11, padding: 8, textAlign: 'center', gap: 4,
              }}>
                <AlertTriangle size={14} />
                {f.error ? String(f.error).slice(0, 70) : 'No clip yet. This line is read over the shot before.'}
              </div>
            )}

            <div style={{ padding: '7px 9px', flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--gray-500)' }}>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>Shot {f.number}</span>
                <span>
                  {f.seconds ? `${f.seconds.toFixed(1)}s` : ''}
                  {f.kind === 'generated' && f.cost ? ` · $${f.cost.toFixed(2)}` : ''}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-700)', lineHeight: 1.45, flex: 1 }}>
                {String(f.script_text || f.purpose || '').slice(0, 90)}
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                <button className="btn btn-outline" disabled={busy}
                  style={{ fontSize: 10, padding: '3px 8px' }}
                  onClick={() => onChoose && onChoose(f)}>
                  {f.kind === 'real' ? 'Change the clip' : 'Use real footage'}
                </button>
                {onUsePhoto ? (
                  <button className="btn btn-outline" disabled={busy}
                    style={{ fontSize: 10, padding: '3px 8px' }}
                    title="Use one of our photographs, with a slow move on it"
                    onClick={() => onUsePhoto(f)}>
                    <Image size={10} style={{ verticalAlign: -1, marginRight: 3 }} />Photo
                  </button>
                ) : null}
                {f.kind !== 'real' && onEdit ? (
                  <button className="btn btn-outline" disabled={busy}
                    style={{ fontSize: 10, padding: '3px 8px' }}
                    title="Change what this shot shows and regenerate it"
                    onClick={() => onEdit(f)}>
                    <Pencil size={10} style={{ verticalAlign: -1, marginRight: 3 }} />Fix it
                  </button>
                ) : null}
                {onInsertBefore ? (
                  <button className="btn btn-ghost" disabled={busy}
                    style={{ fontSize: 10, padding: '3px 6px' }}
                    title="Put another shot in front of this one"
                    onClick={() => onInsertBefore(f, 'before')}>
                    <CornerDownRight size={10} style={{ verticalAlign: -1 }} />+
                  </button>
                ) : null}
                {onInsertBefore ? (
                  <button className="btn btn-ghost" disabled={busy}
                    style={{ fontSize: 10, padding: '3px 6px' }}
                    title="Put another shot straight after this one"
                    onClick={() => onInsertBefore(f, 'after')}>
                    +<CornerDownRight size={10} style={{ verticalAlign: -1 }} />
                  </button>
                ) : null}
                {f.kind === 'real' && f.hasGeneratedTake ? (
                  <button className="btn btn-ghost" disabled={busy}
                    style={{ fontSize: 10, padding: '3px 6px' }}
                    title="Go back to the clip that was generated for this shot"
                    onClick={() => onRevert && onRevert(f)}>
                    <Undo2 size={10} style={{ verticalAlign: -1 }} />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
